import React, { ReactNode } from 'react';

export const configUrl = process.env.REACT_APP_SERVER_URL;
export let checkoutUiUrl = process.env.REACT_APP_CHECKOUT_UI_URL;

export const configUrlMock = 'http://localhost:3004';

export type smsCustomTextType = {
    sms1?: string;
    sms2?: string;
    sms3?: string;
};

export type ChannelConfig = {
    name: string;
    id: number;
    provider?: 'mlb' | 'axs' | 'flash' | 'ticketmaster' | 'veritix' | 'provenue' | 'seatgeek';
    marketingCheckboxOnSignup?: boolean;
    sso?: null | 'ticketmaster' | 'okta' | 'seatgeek' | 'ballpark';
    fullName: string;
    privacyPolicy: string;
    termsOfUse: string;
    isDemo?: boolean;
    canSignupWithAxs?: boolean;
    googleAnalyticsId?: string;
    promoVideoUrl?: string;
    axsPrivacyPolicy?: string;
    axsTermsOfUse?: string;
    smsTextOverrides?: Record<string, smsCustomTextType>;
    faqOverrides?: Record<string, string>;
    logoOverrides?: Record<string, string>;
    backgroundOverrides?: Record<string, string>;
    logoBackgroundOverrides?: Record<string, string>;
    thumbnailOverrides?: Record<string, string>;
    bannerTextOverrides?: Record<string, ReactNode>;
    signupSubTitleOverrides?: Record<string, string>;
    inviteSubHeadingOverrides?: Record<string, string>;
    sliderOnLandingPage?: Record<string, string[]>;
    defaultChannel?: string;
    seatmapUrl?: string;
    seatmapUrlQA?: string;
    biddingUrl?: string;
    biddingUrlQA?: string;
    marketplaceUrl?: string;
    marketplaceUrlQA?: string;
    dropsUrl?: string;
    dropsUrlQA?: string;
    hidePogoseatBranding?: boolean;
    useOptInLoginPage?: boolean;
    // Opt-in checkbox copy: privacy-policy acknowledgement + Program consent instead of the marketing line.
    programConsentOptIn?: boolean;
    optInRedirectUrl?: string;
    optInHero?: {
        headline?: string;
        subtext?: string;
        formHeadline?: string;
        formSubtext?: string;
        accentColor?: string;
        videoUrl?: string;
        // Hero art per breakpoint (mobile = stacked layout: ≤896px page, ≤600px embed); either falls back to
        // the other. Beats the dashboard signup background; a ?source= background override still wins.
        // Desktop art fills the panel cropping from the bottom (keep headlines near the top), unless imageBackground is set.
        imageUrl?: string;
        mobileImageUrl?: string;
        imageAlt?: string;
        // Fill color around the desktop art (e.g. the art's own background); when set the whole image shows, never cropped.
        imageBackground?: string;
        // The config hero image carries its own text/branding: hide the hero logo, headline, subtext and tint.
        imageOnly?: boolean;
        // Logo on the form side, above the sign-up/sign-in tabs (not shown in the embed widget).
        formLogoUrl?: string;
        // Image at the bottom of the form side, e.g. a sponsor lockup (not shown in the embed widget).
        footerImageUrl?: string;
        footerImageAlt?: string;
    };
    // Embeddable signup widget (script-tag popup on a partner's own site).
    // `enabled` exposes /pass/embed + /pass/signup-widget.js for this channel.
    // `allowedOrigins` is the source of truth for which partner sites may frame it
    // (used to drive the nginx `frame-ancestors` CSP; not enforced until that header is wired).
    embed?: {
        enabled?: boolean;
        allowedOrigins?: string[];
    };
};

const salesChannels: Array<ChannelConfig> = [
    //Here we add list of names and ids (name is the part that will be in url e.g. rockets.pogoseat.com/pass
    {
        name: 'rockets',
        id: 38,
        provider: 'axs',
        fullName: 'Houston Rockets',
        isDemo: false,
        googleAnalyticsId: 'G-V64X5QJTTG',
        privacyPolicy: 'https://www.toyotacenter.com/privacy-policy',
        termsOfUse: 'https://www.toyotacenter.com/terms-of-use',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Rockets_TTBvideo.mp4',
        axsPrivacyPolicy: 'https://www.axs.com/about-privacy-policy_US_v8.html',
        axsTermsOfUse: 'https://www.axs.com/about-terms-of-use_US_v2.html',
    },
    {
        name: 'vgk',
        id: 71,
        provider: 'axs',
        defaultChannel: 'attentive',
        marketingCheckboxOnSignup: true,
        isDemo: false,
        fullName: 'Vegas Golden Knights',
        googleAnalyticsId: 'G-4KNSYBF29Y',
        privacyPolicy: 'https://www.nhl.com/goldenknights/team/privacy-policy',
        termsOfUse: 'https://www.nhl.com/goldenknights/team/terms-of-service',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Golden_Knights_TTBvideo.mp4',
        axsPrivacyPolicy: 'https://www.axs.com/about-privacy-policy_US_v8.html',
        axsTermsOfUse: 'https://www.axs.com/about-terms-of-use_US_v2.html',
        smsTextOverrides: {
            studentrush: {
                sms1: 'Welcome to our Student Rush program, where you will receive exclusive savings on last minute Golden Knights tickets',
                sms2: 'How does it work?',
                sms3: 'Watch this short video! Sign up with an active University or College email domain to receive special ticket offers via text.',
            },
        },
        faqOverrides: {
            studentrush:
                '[{"question":"Do I need a mobile phone?","answer":"Yes, this is a mobile-only program."},{"question":"How often will I receive text offers?","answer":"Text offers will be sent the week of the game."},{"question":"How do I sign up as a ‘student’?","answer":"You must sign up with an active “.edu” email address. Your tickets will be sent to this email address."},{"question":"How often will I receive Student text offers?","answer":"Students will receive offers the week of the game."},{"question":"How many Student Tickets can I purchase?","answer":"Students will only be able to purchase up to 2 tickets per event."},{"question":"How do I find my tickets?","answer":"Your tickets will be sent to your AXS Mobile ID. A confirmation e-mail will be sent to you after purchase. The easiest way to access your tickets and enter the game is by downloading the AXS mobile app prior to arrival. Be sure to check your spam folder if you don’t see it."},{"question":"How do I resell or transfer my tickets?","answer":"Tickets purchased via text CANNOT be resold. Any other resale or attempted resale without consent of the Golden Knights is grounds for seizure or cancellation without refund or other compensation. Transfers are allowed."},{"question":"How do I enter the venue?","answer":"Use your digital tickets to enter the venue."},{"question":"How do I print my ticket?","answer":"Tickets are not printable. All you need is the digital ticket on your phone."},{"question":"If I buy tickets, how can my guest get his/her ticket?","answer":"All tickets will be delivered to the phone that registers. If you purchase two tickets, for example, both tickets will be delivered to your phone and sent to your AXS Mobile ID."},{"question":"What happens if my phone dies when I’m trying to enter the venue?","answer":"Visit the box office at the T-Mobile Arena and a representative will assist you."},{"question":"How do I update my payment details?","answer":"Please visit URL to update https://vgk.pogoseat.com/pass/login"}]',
        },
        bannerTextOverrides: {
            studentrush: React.createElement(
                'div',
                null,
                React.createElement(
                    'p',
                    null,
                    'Sign up with an active “.edu” email address to receive exclusive Vegas Golden Knights ticket offers via text throughout the 2022-23 season!',
                ),
            ),
        },
        logoOverrides: {
            studentrush: 'https://cdn.pogoseat.com/images/leagues/nhl/retina/vgk-sr.png',
        },
    },
    {
        name: 'giants',
        id: 20,
        provider: 'mlb',
        isDemo: false,
        fullName: 'San Francisco Giants',
        privacyPolicy: 'https://www.mlb.com/app/ballpark/official-information/privacy-policy',
        termsOfUse: 'https://www.mlb.com/app/ballpark/official-information/terms-of-use',
    },
    {
        name: 'swarm',
        id: 101,
        provider: 'axs',
        marketingCheckboxOnSignup: true,
        isDemo: false,
        fullName: 'Georgia Swarm',
        googleAnalyticsId: 'G-9WS7K71J7L',
        privacyPolicy: 'https://www.georgiaswarm.com/privacy-policy/',
        termsOfUse: 'https://www.georgiaswarm.com/terms/',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Swarm_TTBvideo.mp4',
        axsPrivacyPolicy: 'https://www.axs.com/about-privacy-policy_US_v8.html',
        axsTermsOfUse: 'https://www.axs.com/about-terms-of-use_US_v2.html',
    },
    {
        name: 'reds',
        id: 111,
        provider: 'provenue',
        sso: 'okta',
        isDemo: false,
        fullName: 'Cincinnati Reds',
        googleAnalyticsId: 'G-F080H04F0T',
        privacyPolicy: 'https://mlb.com/app/ballpark/official-information/privacy-policy',
        termsOfUse: 'https://mlb.com/app/ballpark/official-information/terms-of-use',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Pogoseat_TTB_General.mp4',
        hidePogoseatBranding: true,
    },
    {
        name: 'nuggets',
        id: 121,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Denver Nuggets',
        googleAnalyticsId: 'G-8REB9Z54TZ',
        privacyPolicy: 'https://www.ballarena.com/privacy-policy',
        termsOfUse: 'https://www.ballarena.com/terms-conditions',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Nuggets_TTBvideo.mp4',
        isDemo: false,
        smsTextOverrides: {
            premium: {
                sms1: 'Interested in last-minute access to Club Lexus seats?',
                sms2: 'Sure!',
            },
            lastchance: {
                sms1: 'Welcome to Nuggets Last Chance Tickets! Exclusive access to last-minute tickets on game day!',
                sms2: 'Awesome! How does it work?',
                sms3: "Watch this video. It's as easy as sending a text message.",
            },
            studentrush: {
                sms1: 'Welcome to our Student Rush program, where you will receive exclusive access to tickets. Sign In with an active University or College email ".edu" or create a new Ticketmaster account with your ".edu" email to receive special ticket offers via text.',
            },
        },
        faqOverrides: {
            premium:
                '[{"question":"Where is Club Lexus located?","answer":"The entrance for Club Lexus is located near the corner of Chopper Circle and 11th."},{"question":"What do Club Lexus seats include?","answer":"Club Lexus seats come with all-inclusive food, beer, wine and liquor in the club space."},{"question":"When is the club open?","answer":"Club Lexus opens 90 minutes before the game and is open until 60 minutes after the game."},{"question":"Do I need a mobile phone?","answer":"Yes, this is a mobile-only program."},{"question":"How often will I receive text offers?","answer":"Text offers will be sent the week of the game."},{"question":"How do I find my tickets?","answer":"Your tickets will be sent to your Ticketmaster Account Manager account. A confirmation e-mail will be sent to you after purchase. Be sure to check your spam folder if you don’t see it."},{"question":"How do I resell or transfer my tickets? ","answer":"Tickets purchased via text CANNOT be resold or transferred. Any other resale or attempted resale without consent of the Denver Nuggets is grounds for seizure or cancellation without refund or other compensation. "},{"question":"How do I enter the venue?","answer":"Use your digital tickets to enter the venue."},{"question":"How do I print my ticket?","answer":"Tickets are not printable. All you need is the digital ticket on your phone. For more info visit: https://am.ticketmaster.com/kse/contact-us"},{"question":"If I buy tickets, how can my guest get his/her ticket?","answer":"All tickets will be delivered to the phone that registers. If you purchase two tickets, for example, both tickets will be delivered to your phone and sent to your Account Manager ticketing account."},{"question":"Can I bring different people with me to each game?","answer":"Yes, different people can attend each game, but the primary account holder must be present because the tickets are only accessible through the mobile device of the primary account holder via their Account Manager digital ticketing account."},{"question":"What happens if my phone dies when I’m trying to enter the venue?","answer":"Visit the box office at the Ball Arena and a representative will assist you."},{"question":"How do I update my payment details?","answer":"https://nuggets.pogoseat.com"}]',
            studentrush:
                '[{"question": "Do I need a mobile phone?", "answer": "Yes, this is a mobile-only program."}, {	"question": "How do I sign up as a ‘student?‘", "answer": "You can either update your existing Ticketmaster account info with your ‘edu‘ email or create a new Ticketmaster account with your ‘edu‘ email."}, {	"question": "How often will I receive Student text offers?", "answer": "Frequency of offers can vary. Students will receive offers the week of the game."}, {	"question": "Is Student Rush offer available on the day of the game at the Ticket Office?", "answer": "No, Student Rush tickets are only available on your mobile device on a first come first serve basis."}, {	"question": "How do I find my tickets?", "answer": "Your tickets will be sent to your Ticketmaster Account Manager account. A confirmation e-mail will be sent to you after purchase. Be sure to check your spam folder if you don\'t see it."}, {		"question": "How do I resell or transfer my tickets?", "answer": "Tickets purchased via text CANNOT be resold. Any other resale or attempted resale without consent of the Denver Nuggets is grounds for seizure or cancellation without refund or other compensation."}, {	"question": "How do I enter the venue?", "answer": "Use your digital tickets located on your Nuggets Account Manager or Denver Nuggets App to enter Ball Arena."}, {	"question": "How do I print my ticket?", "answer": "Tickets are not printable. All you need is the digital ticket on your phone."}, {	"question": "If I buy tickets, how can my guest get their ticket?", "answer": "All tickets will be delivered to the phone that registers. If you purchase two tickets, for example, both tickets will be delivered to your phone and sent to your Account Manager ticketing account. You and your guest will need to enter the building and scan in together."}, {	"question": "Can I bring different people with me to each game?", "answer": "Yes, different people can attend each game, but the primary account holder must be present because the tickets are only accessible through the mobile device of the primary account holder via their Account Manager digital ticketing account."}, {	"question": "What happens if my phone dies when I\'m trying to enter the venue?", "answer": "Visit the Ticket Office. You will need to present a valid ID at the ticket office window."}, {	"question": "How do I update my payment details?", "answer": "https://nuggets.pogoseat.com"}]',
            lastchance:
                '[{"question":"Do I need a mobile phone?","answer":"Yes, this is a mobile-only program."},{"question":"How often will I receive text offers?","answer":"Text offers will be sent the week of the game."},{"question":"What\'s the difference in Buzzer Beater and Last Chance Tickets","answer":"Buzzer Beater offers are typically $20 tickets in the Upper Bowl and Last Chance Tickets are ticket offers in the Lower Bowl. You can only sign up to one program. Please note Playoff tickets are NOT $20."},{"question":"How do I find my tickets?","answer":"Your tickets will be sent to your Ticketmaster Account Manager account. A confirmation e-mail will be sent to you after purchase. Be sure to check your spam folder if you don\'t see it."},{"question":"How do I resell or transfer my tickets?","answer":"Tickets purchased via text CANNOT be resold or transferred. Any other resale or attempted resale without consent of the Denver Nuggets is grounds for seizure or cancellation without refund or other compensation."},{"question":"How do I enter the venue?","answer":"Use your digital tickets to enter the venue."},{"question":"How do I print my ticket?","answer":"Tickets are not printable. All you need is the digital ticket on your phone. For more info visit: https://am.ticketmaster.com/kse/contact-us"},{"question":"If I buy tickets, how can my guest get his/her ticket?","answer":"All tickets will be delivered to the phone that registers. If you purchase two tickets, for example, both tickets will be delivered to your phone and sent to your Account Manager ticketing account."},{"question":"Can I bring different people with me to each game?","answer":"Yes, different people can attend each game, but the primary account holder must be present because the tickets are only accessible through the mobile device of the primary account holder via their Account Manager digital ticketing account."},{"question":"What happens if my phone dies when I\'m trying to enter the venue?","answer":"Visit the box office at the Ball Arena and a representative will assist you."},{"question":"How do I update my payment details?","answer":"https://nuggets.pogoseat.com"}]',
        },
        logoOverrides: {
            premium: 'https://cdn.pogoseat.com/images/web/partners/tm/Nugs%20Lexus%20logo.png',
            lastchance:
                'https://pogo-prod-oneuploadsproddb556cb8-syd0aj1xvqwn.s3.us-west-1.amazonaws.com/sc-121/logo/2c2b5477086e7cb7b85df8f727218e0a11af0c6c0abe6f63778334ee2fa02814.png',
            CHCO: 'https://pogo-prod-oneuploadsproddb556cb8-syd0aj1xvqwn.s3.us-west-1.amazonaws.com/sc-121/logo/78ea40efc061230b43f8f5b7994c5b85040947198853003f14c1c0bf8dcd8e15.png',
            studentrush:
                'https://pogo-prod-oneuploadsproddb556cb8-syd0aj1xvqwn.s3.us-west-1.amazonaws.com/sc-121/logo/5af2620db53349382e89c0e6191f990257c88a1d75887f9f33fd517f862031e7.png',
        },
        thumbnailOverrides: {
            premium: 'https://cdn.pogoseat.com/images/web/partners/tm/Nuggets_signuppage_mockup_club_lexus.jpg',
        },
        backgroundOverrides: {
            premium: 'https://cdn.pogoseat.com/images/web/partners/tm/Nuggets_Club_Lexus_2023_BTY_563001.jpg',
            lastchance:
                'https://pogo-prod-oneuploadsproddb556cb8-syd0aj1xvqwn.s3.us-west-1.amazonaws.com/sc-121/logo/c9217d360ec7740a0eaad66faaff6ab75044a09b6b9545ce7e2cb9aa9196bd9a.png',
        },
        logoBackgroundOverrides: {
            premium: '#FEC524',
        },
        sliderOnLandingPage: {
            premium: [
                'https://cdn.pogoseat.com/images/web/partners/tm/Nuggets_Club_Lexus_2023_BTY_563001.jpg',
                'https://cdn.pogoseat.com/images/web/partners/tm/Nuggets_Club_Lexus_2023_BTY_577001.jpg',
                'https://cdn.pogoseat.com/images/web/partners/tm/Nuggets_Club_Lexus_2023_BTY_581001.jpg',
                'https://cdn.pogoseat.com/images/web/partners/tm/Nuggets_Club_Lexus_2023_BTY_585001-(1).jpg',
                'https://cdn.pogoseat.com/images/web/partners/tm/Nuggets_Club_Lexus_2023_BTY_610001-(1).jpg',
                'https://cdn.pogoseat.com/images/web/partners/tm/Nuggets_Club_Lexus_2023_BTY_623001.jpg',
            ],
        },
        dropsUrl: 'https://nuggetsdrops.pogoseat.com/',
        dropsUrlQA: 'https://nuggetsdropsqa.pogoseat.com/',
        seatmapUrl: 'https://nuggetstickets.pogoseat.com',
        seatmapUrlQA: 'https://nuggetsticketsqa.pogoseat.com',
    },
    {
        name: 'avs',
        id: 122,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Colorado Avalanche',
        googleAnalyticsId: 'G-7FB77060W9',
        privacyPolicy: 'https://www.nhl.com/avalanche/info/privacy-policy',
        termsOfUse: 'https://www.nhl.com/info/terms-of-service',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Avs_TTBvideo.mp4',
        isDemo: false,
        smsTextOverrides: {
            premium: {
                sms1: 'Interested in last-minute access to Club Lexus seats?',
                sms2: 'Sure!',
            },
            studentrush: {
                sms1: 'Welcome to our College Power Play program, where you will receive exclusive access to tickets. Sign In with an active University or College email ".edu" or create a new Ticketmaster account with your ".edu" email to receive special ticket offers via text.',
                sms2: 'How does it work?',
                sms3: "Watch this video, it's as easy as sending a text message!",
            },
        },
        faqOverrides: {
            premium:
                '[{"question":"Where is Club Lexus located?","answer":"The entrance for Club Lexus is located near the corner of Chopper Circle and 11th."},{"question":"What do Club Lexus seats include?","answer":"Club Lexus seats come with all-inclusive food, beer, wine and liquor in the club space."},{"question":"When is the club open?","answer":"Club Lexus opens 90 minutes before the game and is open until 60 minutes after the game."},{"question":"Do I need a mobile phone?","answer":"Yes, this is a mobile-only program."},{"question":"How often will I receive text offers?","answer":"Text offers will be sent the week of the game."},{"question":"How do I find my tickets?","answer":"Your tickets will be sent to your Ticketmaster Account Manager account. A confirmation e-mail will be sent to you after purchase. Be sure to check your spam folder if you don’t see it."},{"question":"How do I resell or transfer my tickets? ","answer":"Tickets purchased via text CANNOT be resold or transferred. Any other resale or attempted resale without consent of the Colorado Avalanche is grounds for seizure or cancellation without refund or other compensation."},{"question":"How do I enter the venue?","answer":"Use your digital tickets to enter the venue."},{"question":"How do I print my ticket?","answer":"Tickets are not printable. All you need is the digital ticket on your phone. For more info visit: https://am.ticketmaster.com/kse/contact-us"},{"question":"If I buy tickets, how can my guest get his/her ticket?","answer":"All tickets will be delivered to the phone that registers. If you purchase two tickets, for example, both tickets will be delivered to your phone and sent to your Account Manager ticketing account."},{"question":"Can I bring different people with me to each game?","answer":"Yes, different people can attend each game, but the primary account holder must be present because the tickets are only accessible through the mobile device of the primary account holder via their Account Manager digital ticketing account."},{"question":"What happens if my phone dies when I’m trying to enter the venue?","answer":"Visit the box office at the Ball Arena and a representative will assist you."},{"question":"How do I update my payment details?","answer":"https://avs.pogoseat.com"},{"question":"Is parking included?","answer":"Parking does not come included with single game Club Lexus purchases."}]',
            studentrush:
                '[{"question":"Do I need a mobile phone?","answer":"Yes, this is a mobile-only program."},{"question":"How do I sign up as a student?","answer":"Your email address must be associated with your .edu email domain."},{"question":"How often will I receive Student text offers?","answer":"Students will receive offers the week of the game."},{"question":"How do I find my tickets?","answer":"Your tickets will be sent to your Ticketmaster Account Manager account. A confirmation e-mail will be sent to you after purchase. Be sure to check your spam folder if you don`t see it."},{"question":"How do I resell or transfer my tickets?","answer":"Tickets purchased via text CANNOT be resold or transferred. Any other resale or attempted resale without consent of the Colorado Avalanche is grounds for seizure or cancellation without refund or other compensation."},{"question":"How do I enter the venue?","answer":"Use your digital tickets to enter the venue."},{"question":"How do I print my ticket?","answer":"Tickets are not printable. All you need is the digital ticket on your phone. For more info visit: https://am.ticketmaster.com/kse/contact-us"},{"question":"If I buy tickets, how can my guest get his/her ticket?","answer":"All tickets will be delivered to the phone that registers. If you purchase two tickets, for example, both tickets will be delivered to your phone and sent to your Account Manager ticketing account."},{"question":"Can I bring different people with me to each game?","answer":"Yes, different people can attend each game, but the primary account holder must be present because the tickets are only accessible through the mobile device of the primary account holder via their Account Manager digital ticketing account."},{"question":"What happens if my phone dies when I`m trying to enter the venue?","answer":"Visit the box office at the Ball Arena and a representative will assist you."},{"question":"How do I update my payment details?","answer":"https://avs.pogoseat.com"}]',
        },
        logoOverrides: {
            premium: 'https://cdn.pogoseat.com/images/web/partners/tm/Avs%20Lexus%20logo.png',
            AmateurHockey: 'https://cdn.pogoseat.com/images/web/partners/tm/CA_YouthHocky-logo.png',
            studentrush:
                'https://pogo-uploads-prod.s3.us-west-1.amazonaws.com/sales_channel/122/CA_2425_CollegePPLogo.png',
            CHCO: 'https://pogo-prod-oneuploadsproddb556cb8-syd0aj1xvqwn.s3.us-west-1.amazonaws.com/sc-122/logo/021b5aecf613268ad8286d85bd295ed87ed966776c9d5f9a746e301f9e72cbd3.png',
        },
        backgroundOverrides: {
            premium: 'https://cdn.pogoseat.com/images/web/partners/tm/Nuggets_Club_Lexus_2023_BTY_563001.jpg',
            AmateurHockey: 'https://cdn.pogoseat.com/images/web/partners/tm/ah-mountain-bg.jpg',
            studentrush: 'https://pogo-uploads-prod.s3.us-west-1.amazonaws.com/sales_channel/122/Avs-background.jpg',
        },
        sliderOnLandingPage: {
            premium: [
                'https://cdn.pogoseat.com/images/web/partners/tm/AVS_Lexus_BTY_003.jpg',
                'https://cdn.pogoseat.com/images/web/partners/tm/AVS_Lexus_BTY_017.jpg',
                'https://cdn.pogoseat.com/images/web/partners/tm/AVS_Lexus_BTY_025.jpg',
                'https://cdn.pogoseat.com/images/web/partners/tm/AVS_Lexus_BTY_029.jpg',
                'https://cdn.pogoseat.com/images/web/partners/tm/AVS_Lexus_BTY_041.jpg',
                'https://cdn.pogoseat.com/images/web/partners/tm/AVS_Lexus_BTY_050.jpg',
                'https://cdn.pogoseat.com/images/web/partners/tm/AVS_Lexus_BTY_059.jpg',
                'https://cdn.pogoseat.com/images/web/partners/tm/AVS_Lexus_BTY_062.jpg',
                'https://cdn.pogoseat.com/images/web/partners/tm/AVS_Lexus_BTY_088.jpg',
                'https://cdn.pogoseat.com/images/web/partners/tm/AVS_Lexus_BTY_112.jpg',
            ],
        },
        seatmapUrl: 'https://avstickets.pogoseat.com',
        seatmapUrlQA: 'https://avsticketsqa.pogoseat.com',
    },
    {
        name: 'rapids',
        id: 123,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Colorado Rapids',
        googleAnalyticsId: 'G-LM12LW37K6',
        privacyPolicy: 'https://www.mlssoccer.com/legal/privacy-policy',
        termsOfUse: 'https://www.mlssoccer.com/legal/terms-of-service',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Rapids_TTBvideo.mp4',
        isDemo: false,
        logoOverrides: {
            neighborhood: 'https://cdn.pogoseat.com/images/web/partners/tm/Rapids_SupportLocal.png',
            CRYSC: 'https://cdn.pogoseat.com/images/web/partners/tm/rapid-ysc.png',
        },
        logoBackgroundOverrides: {
            neighborhood: '#7b2e37',
        },
        smsTextOverrides: {
            CRYSC: {
                sms1: 'Calling CRYSC families! Sign up for the Colorado Rapids Pitch Pass to get exclusive discounted ticket offers via text message! Register today and get $5 off your first purchase.',
            },
        },
        seatmapUrl: 'https://rapidstickets.pogoseat.com',
    },
    {
        name: 'mammoth',
        id: 124,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Colorado Mammoth',
        googleAnalyticsId: 'G-STSVX2DWVF',
        privacyPolicy: 'https://coloradomammoth.com/privacy-policy/',
        termsOfUse: 'https://coloradomammoth.com/terms-and-conditions/',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Mammoth_TTBvideo.mp4',
        isDemo: false,
    },
    {
        name: 'pistons',
        id: 125,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Detroit Pistons',
        googleAnalyticsId: 'G-50378ZEPL9',
        privacyPolicy: 'https://www.nba.com/privacy-policy',
        termsOfUse: 'https://www.nba.com/termsofuse',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Pistons_TTBvideo.mp4',
        isDemo: false,
        seatmapUrl: 'https://pistonstickets.pogoseat.com',
        seatmapUrlQA: 'https://pistonsticketsqa.pogoseat.com',
    },
    {
        name: 'raiders',
        id: 127,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Las Vegas Raiders',
        googleAnalyticsId: '',
        privacyPolicy: 'https://www.raiders.com/about/privacy-policy',
        termsOfUse: 'https://am.ticketmaster.com/raiders/terms',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Raiders_TTBvideo.mp4',
        isDemo: false,
    },
    {
        name: 'lakers',
        id: 129,
        provider: 'veritix',
        marketingCheckboxOnSignup: true,
        fullName: 'LA Lakers',
        googleAnalyticsId: 'G-M05T95GSTB',
        privacyPolicy: 'https://www.nba.com/privacy-policy',
        termsOfUse: 'https://www.nba.com/termsofuse',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/lakers_TTBvideo.mp4',
        axsPrivacyPolicy: 'https://www.axs.com/about-privacy-policy_US_v8.html',
        axsTermsOfUse: 'https://www.axs.com/about-terms-of-use_US_v2.html',
        isDemo: false,
    },
    {
        name: 'astros',
        id: 109,
        provider: 'provenue',
        sso: 'okta',
        fullName: 'Houston Astros',
        googleAnalyticsId: 'G-5NE5K36NLD',
        privacyPolicy: 'https://www.mlb.com/astros/official-information/privacy-policy/offline',
        termsOfUse: 'https://www.mlb.com/astros/tickets/terms-and-conditions',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Pogoseat_TTB_General.mp4',
        isDemo: false,
    },
    {
        name: 'nycfc',
        id: 130,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'New York City FC',
        googleAnalyticsId: 'G-X5GBH926CX',
        privacyPolicy: 'https://www.mlssoccer.com/legal/privacy-policy',
        termsOfUse: 'https://www.mlssoccer.com/legal/terms-of-service',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/NYCFC_TTBvideo.mp4',
        isDemo: false,
    },
    {
        name: 'bruins',
        id: 132,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Boston Bruins',
        googleAnalyticsId: 'G-P3L9C1FKEY',
        privacyPolicy: 'https://www.tdgarden.com/privacy-policy',
        termsOfUse: 'https://www.tdgarden.com/privacy-policy',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Bruins_TTBvideo.mp4',
        isDemo: false,
        useOptInLoginPage: true,
        optInHero: {
            videoUrl: 'https://cdn.pogoseat.com/images/videos/Bruins_TTBvideo.mp4',
        },
        optInRedirectUrl: 'https://www.nhl.com/bruins/',
        defaultChannel: 'twilioRcs',
        embed: {
            enabled: true,
            // TODO: add the exact origin(s) Bruins will embed on (e.g. 'https://www.nhl.com')
            // before wiring nginx frame-ancestors. Empty = not yet restricted.
            allowedOrigins: [],
        },
    },
    {
        name: 'celtics',
        id: 133,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Boston Celtics',
        googleAnalyticsId: 'G-2JTTCBQCCP',
        privacyPolicy: 'https://www.nba.com/privacy-policy',
        termsOfUse: 'https://www.nba.com/termsofuse',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Celtics_TTBvideo.mp4',
        isDemo: false,
    },
    {
        name: 'dlc',
        id: 135,
        provider: 'veritix',
        marketingCheckboxOnSignup: true,
        fullName: "Lee's Family Forum",
        googleAnalyticsId: 'G-D6J07W3ZJS',
        privacyPolicy: 'https://www.foleyentertainmentgroup.com/privacy-policy/',
        termsOfUse: 'https://www.foleyentertainmentgroup.com/terms-of-use/',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/dlc_TTBvideo.mp4',
        axsPrivacyPolicy: 'https://www.axs.com/about-privacy-policy_US_v8.html',
        axsTermsOfUse: 'https://www.axs.com/about-terms-of-use_US_v2.html',
        canSignupWithAxs: true,
        isDemo: false,
        smsTextOverrides: {
            studentrush: {
                sms1: "Welcome to our Student Rush program, where you will receive exclusive savings on last minute tickets at Lee's Family Forum",
                sms2: 'How does it work?',
                sms3: 'Watch this short video! Sign up with an active University or College email domain to receive special ticket offers via text.',
            },
        },
        faqOverrides: {
            studentrush:
                '[{"question":"Do I need a mobile phone?","answer":"Yes, this is a mobile-only program."},{"question":"How often will I receive text offers?","answer":"Text offers will be sent the week of the game."},{"question":"How do I sign up as a ‘student’?","answer":"You must sign up with an active “.edu” email address. Your tickets will be sent to this email address."},{"question":"How often will I receive Student text offers?","answer":"Students will receive offers the week of the game."},{"question":"How many Student Tickets can I purchase?","answer":"Students will only be able to purchase up to 6 tickets per event."},{"question":"How do I find my tickets?","answer":"Your tickets will be sent to your AXS Mobile ID. A confirmation e-mail will be sent to you after purchase. The easiest way to access your tickets and enter the game is by downloading the AXS mobile app prior to arrival. Be sure to check your spam folder if you don’t see it."},{"question":"How do I resell or transfer my tickets?","answer":"Tickets purchased via text CANNOT be resold. Any other resale or attempted resale without consent of Lee’s Family Forum is grounds for seizure or cancellation without refund or other compensation. Transfers are allowed."},{"question":"How do I enter the venue?","answer":"Use your digital tickets to enter the venue."},{"question":"How do I print my ticket?","answer":"Tickets are not printable. All you need is the digital ticket on your phone."},{"question":"If I buy tickets, how can my guest get his/her ticket?","answer":"All tickets will be delivered to the phone that registers. If you purchase two tickets, for example, both tickets will be delivered to your phone and sent to your AXS Mobile ID."},{"question":"What happens if my phone dies when I’m trying to enter the venue?","answer":"Visit the box office at the T-Mobile Arena and a representative will assist you."},{"question":"How do I update my payment details?","answer":"Please visit URL to update https://dlc.pogoseat.com/pass/login"}]',
        },
        bannerTextOverrides: {
            studentrush: React.createElement(
                'div',
                null,
                React.createElement(
                    'p',
                    null,
                    'Sign up with an active “.edu” email address to receive exclusive Lee’s Family Forum ticket offers via text throughout the 2022-23 season!',
                ),
                React.createElement(
                    'p',
                    null,
                    'Sign up before 3/31/23 and be entered into a drawing for two (2) HSK jerseys and two (2) GLI Courtside Seats to a future home game!',
                ),
            ),
        },
    },
    {
        id: 142,
        name: 'stars',
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Dallas Stars',
        googleAnalyticsId: 'G-0Z8HSNYGBE',
        privacyPolicy: 'https://www.nhl.com/stars/team/privacy-policy',
        termsOfUse: 'https://www.nhl.com/info/terms-of-service',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Stars_TTBvideo.mp4',
        isDemo: false,
    },
    {
        name: 'breakawaycarolina',
        id: 144,
        marketingCheckboxOnSignup: true,
        fullName: 'Breakaway Carolina',
        googleAnalyticsId: 'G-8K8Z4LHW4L',
        privacyPolicy: 'https://www.breakawayfestival.com/terms-conditions',
        termsOfUse: 'https://www.breakawayfestival.com/terms-conditions',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/breakaway_TTBvideo.mp4',
        isDemo: false,
        signupSubTitleOverrides: {
            studentrush: 'Sign up with your phone number. We will text you when tickets are available.',
        },
        bannerTextOverrides: {
            studentrush:
                'Breakaway Carolina has made it easier for students to buy last minute tickets and it’s as simple as sending a text message.',
        },
        faqOverrides: {
            studentrush:
                '[{"question":"Do I need a mobile phone?","answer":"Yes, this is a mobile-only program."},{"question":"How often will I receive text offers?","answer":"Text offers will be sent upon signing up and texting SHOW ALL."},{"question":"How do I sign up as a ‘student’?","answer":"You must sign up with an active “.edu” email address. Your tickets will be sent to this email address."},{"question":"How often will I receive Student text offers?","answer":"Students will receive an offer when signing up and texting SHOW ALL."},{"question":"How many Student Tickets can I purchase?","answer":"Students will only be able to purchase up to 10 tickets per event."},{"question":"How do I find my tickets?","answer":"Your tickets will be sent to your email address 2 weeks prior to the festival. A confirmation e-mail will be sent to you after purchase. Be sure to check your spam folder if you don’t see it."},{"question":"How do I resell or transfer my tickets?","answer":"Tickets purchased via text CANNOT be resold. Any other resale or attempted resale without consent of Prime Social Group is grounds for seizure or cancellation without refund or other compensation. Transfers are NOT allowed."},{"question":"How do I enter the venue?","answer":"Use your digital tickets to enter the venue."},{"question":"How do I print my ticket?","answer":"Tickets are not printable. All you need is the digital ticket on your phone."},{"question":"If I buy tickets, how can my guest get his/her ticket?","answer":"All tickets will be delivered to the email that registers. If you purchase two tickets, for example, both tickets will be delivered to your email address."},{"question":"What happens if my phone dies when I’m trying to enter the venue?","answer":"Visit the box office and a representative will assist you."},{"question":"How do I update my payment details?","answer":"Please visit URL to update https://breakawaycarolina.pogoseat.com/pass/login"}]',
        },
    },
    {
        name: 'cse',
        id: 145,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Calgary Sports and Entertainment',
        googleAnalyticsId: 'G-EKWNQ4YSWG',
        privacyPolicy: 'https://www.nhl.com/flames/team/privacy-policy',
        termsOfUse: 'https://www.nhl.com/info/terms-of-service',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Flames_TTBvideo.mp4',
        isDemo: false,
        faqOverrides: {
            studentrushca:
                '[{"question":"Do I need a mobile phone?","answer":"Yes, this is a mobile-only program."},{"question":"What classifies as a \'Student\'?","answer":"A fan that must be a University or College Student with a valid student email."},{"question":"How do I sign up as a "Student?","answer":"Your email address on Ticketmaster.ca must be associated with your University or College email domain."},{"question":"How many tickets are there for Students each game?","answer":"Tickets are limited and will be served on a first-come basis. Student Rush inventory will sell out."},{"question":"How often will I receive Student text offers?","answer":"Frequency of offers can very. There will not be Student Rush offers for every Flames game."},{"question":"Which type of tickets are available to Students?","answer":"Students will receive access to tickets at a discounted rate."},{"question":"How do I find my tickets?","answer":"Your tickets will be sent to your Ticketmaster Account Manager account. A confirmation e-mail will be sent to you after purchase. Be sure to check your spam folder if you don\'t see it."},{"question":"How do I resell or transfer my tickets?","answer":"Tickets purchased via text CANNOT be resold. Any other resale or attempted resale without consent of the Calgary Flames is grounds for seizure or cancellation without refund or other compensation."},{"question":"How do I enter the venue?","answer":"Use your digital tickets on your My Flames Account or Flames App to enter the Scotiabank Saddledome."},{"question":"How do I print my ticket?","answer":"Tickets are not printable. All you need is the digital ticket on your phone."},{"question":"If I buy tickets, how can my guest get his/her ticket?","answer":"All tickets will be delivered to the phone that registers. If you purchase two tickets, for example, both tickets will be delivered to your phone and sent to your Account Manager ticketing account. You and your guest will need to enter the building and scan in together."},{"question":"Can I bring different people with me to each game?","answer":"Yes, different people can attend each game, but the primary account holder must be present because the tickets are only accessible through the mobile device of the primary account holder via their Account Manager digital ticketing account."},{"question":"What happens if my phone dies when I\'m trying to enter the venue?","answer":"Visit the Ticket Service Desk at Scotiabank Saddledome (or McMahon Stadium for Stampeders) and a representative will assist you."},{"question":"How do I update my payment details?","answer":"https://cse.pogoseat.com/pass/login?source=studentrushca"}]',
        },
        smsTextOverrides: {
            studentrushca: {
                sms1: 'Welcome to our Student Rush program, where you will receive exclusive savings on last minute tickets for the Flames, Hitmen, Roughnecks, and Wranglers!',
                sms2: 'How does it work?',
                sms3: 'Watch this short video! Sign in with an active University or College email domain to receive special ticket offers via text.',
            },
        },
        logoOverrides: {
            studentrushca:
                'https://pogo-uploads-prod.s3.us-west-1.amazonaws.com/sales_channel/145/CSE-logos-student-rush3.png',
        },
        thumbnailOverrides: {
            studentrushca:
                'https://pogo-prod-oneuploadsproddb556cb8-syd0aj1xvqwn.s3.us-west-1.amazonaws.com/sc-145/banner/98993ce5f4188da023c86b6a2378979a488973713e502b60f06510cce6ccf7fb.png',
        },
        logoBackgroundOverrides: {
            studentrushca: '#ffffff',
        },
        seatmapUrl: 'https://csetickets.pogoseat.com',
    },
    {
        name: 'steelers',
        id: 147,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Pittsburgh Steelers',
        googleAnalyticsId: 'G-X3L0EVQEE4',
        privacyPolicy: 'https://www.steelers.com/legal/privacy-policy/',
        termsOfUse: 'https://www.steelers.com/legal/terms-of-use/',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Steelers_TTBvideo.mp4',
        isDemo: false,
        inviteSubHeadingOverrides: {
            studentrush: 'Invite your friends to access Student Rush tickets (Standing Room Only)',
        },
        biddingUrl: 'https://steelersbids.pogoseat.com',
        biddingUrlQA: 'https://steelersbidsqa.pogoseat.com',
    },
    {
        name: 'winnipegjets',
        id: 148,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Winnipeg Jets',
        googleAnalyticsId: 'G-ZMR325WKC5',
        privacyPolicy: 'https://www.nhl.com/info/privacy-policy',
        termsOfUse: 'https://www.nhl.com/info/terms-of-service',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Jets_TTBvideo.mp4',
        isDemo: false,
        faqOverrides: {
            studentrushca:
                '[{"question":"Do I need a mobile phone?","answer":"Yes, this is a mobile-only program."},{"question":"What classifies as a ‘student’?","answer":"A fan that must be a University or College Student or Alumni with a valid student or Alumni email"},{"question":"How do I sign up as a ‘student’?","answer":"Your email address on Ticketmaster.ca must be associated with your University or College email domain."},{"question":"How often will I receive Student text offers?","answer":"Frequency of offers can very. There will not be Student Rush offers for every game Winnipeg Jets  game. If you do not receive a Student Rush offer by 1PM on the day of the game, there will not be a Student Rush offer for that specific game."},{"question":"How many tickets are there for Students each game?","answer":"Tickets are limited and will be served on a first-come basis. Student Rush inventory will sell out."},{"question":"What type of Monthly Giveaways will there be for Students?","answer":"Students will be entered for a chance to win a pair of complimentary tickets every month."},{"question":"How do I find my tickets?","answer":"Your tickets will be sent to your Ticketmaster Account Manager account. A confirmation e-mail will be sent to you after purchase. Be sure to check your spam folder if you don’t see it."},{"question":"How do I resell or transfer my tickets?","answer":"Tickets purchased via text CANNOT be resold or transferred. Any other resale or attempted resale without consent of the Winnipeg Jets is grounds for cancellation without refund or other compensation."},{"question":"How do I enter the venue?","answer":"Use your digital tickets on your My Jets Account or Jets360 App to enter the Canada Life Centre."},{"question":"How do I print my ticket?","answer":"Tickets are not printable. All you need is the digital ticket on your phone."},{"question":"If I buy tickets, how can my guest get his/her ticket?","answer":"All tickets will be delivered to the phone that registers. If you purchase two tickets, for example, both tickets will be delivered to your phone and sent to your Account Manager ticketing account. You and your guest will need to enter the building and scan in together"},{"question":"Can I bring different people with me to each game?","answer":"Yes, different people can attend each game, but the primary account holder must be present because the tickets are only accessible through the mobile device of the primary account holder via their Account Manager digital ticketing account."},{"question":"What happens if my phone dies when I’m trying to enter the venue?","answer":"Visit the Canada Life Centre Box Office located at street level at the corner of Portage Avenue and Donald Street in the main atrium (Gate 1)."},{"question":"How do I update my payment details?","answer":"https://winnipegjets.pogoseat.com"}]',
        },
        smsTextOverrides: {
            studentrushca: {
                sms1: 'Welcome to our Student Rush program, where you will receive exclusive savings on last minute tickets.',
                sms2: 'How does it work?',
                sms3: 'Watch this short video! Sign in with an active University or College email domain to receive special ticket offers via text.',
            },
            playoff: {
                sms1: 'Welcome to Standby Playoff Tickets where you will receive access to last minute playoff tickets!',
                sms2: 'How does it work?',
                sms3: 'Watch this video, it’s as easy as sending a text message!',
            },
        },
        logoOverrides: {
            playoff: 'https://cdn.pogoseat.com/images/web/partners/tm/2324JETS108-12_Standby-Playoff-Tickets_v1.png',
            studentrushca: 'https://cdn.pogoseat.com/images/web/partners/tm/jets-sr.png',
            lastminute: 'https://cdn.pogoseat.com/images/web/partners/tm/Standby-Tickets_last_minute.png',
        },
        logoBackgroundOverrides: {
            playoff: '#fcfcfc',
        },
        backgroundOverrides: {
            playoff:
                'https://cdn.pogoseat.com/images/web/partners/tm/2324JETS108-12_Standby-Playoff-Tickets_WEBSKIN_v1.jpg',
            studentrushca: 'https://cdn.pogoseat.com/images/web/partners/tm/Jets-Background-Image3.jpg',
        },
        seatmapUrl: 'https://winnipegjetstickets.pogoseat.com',
    },
    {
        name: 'canadiens',
        id: 149,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Montreal Canadiens',
        googleAnalyticsId: 'G-FXPBX7VTGQ',
        privacyPolicy: 'https://www.nhl.com/canadiens/team/privacy-policy',
        termsOfUse: 'https://www.nhl.com/info/terms-of-service',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Canadiens_TTBvideo.mp4',
        isDemo: false,
    },
    {
        name: 'cruise',
        id: 151,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Motor City Cruise',
        googleAnalyticsId: 'G-1R2V4C8JRD',
        privacyPolicy: 'https://www.nba.com/privacy-policy',
        termsOfUse: 'https://www.nba.com/termsofuse',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Cruise_TTBvideo.mp4',
        isDemo: false,
        seatmapUrl: 'https://cruisetickets.pogoseat.com',
    },
    {
        name: 'theplayers',
        id: 152,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'The Players',
        googleAnalyticsId: 'G-5YCWR525RQ',
        privacyPolicy: 'https://www.pgatour.com/company/privacy-policy',
        termsOfUse: 'https://www.pgatour.com/company/tos',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Pogoseat_TTB_General.mp4',
        isDemo: false,
    },
    {
        name: 'angels',
        id: 153,
        provider: 'provenue',
        sso: 'ballpark',
        fullName: 'Los Angeles Angels',
        googleAnalyticsId: 'G-NBGCLKE6HN',
        privacyPolicy: 'https://www.mlb.com/app/ballpark/official-information/privacy-policy',
        termsOfUse: 'https://www.mlb.com/app/ballpark/official-information/terms-of-use',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Pogoseat_TTB_General.mp4',
        isDemo: false,
        hidePogoseatBranding: true,
    },
    {
        id: 154,
        name: 'fedexchampionship',
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Fedex Championship',
        googleAnalyticsId: 'G-ESD8R3874X',
        privacyPolicy: 'https://www.pgatour.com/company/privacy-policy',
        termsOfUse: 'https://www.pgatour.com/company/tos',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Pogoseat_TTB_General.mp4',
        isDemo: false,
    },
    {
        name: 'nfl',
        id: 158,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'NFL',
        googleAnalyticsId: 'G-4H0JY89LPE',
        privacyPolicy: 'https://www.nfl.com/legal/privacy',
        termsOfUse: 'https://www.nfl.com/legal/terms',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Steelers_TTBvideo.mp4',
        isDemo: false,
    },
    {
        id: 155,
        name: 'psulogin',
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Penn State',
        privacyPolicy: 'https://policy.psu.edu/policies/ad53',
        termsOfUse: 'https://policy.psu.edu/policies/ad53',
        isDemo: false,
        marketplaceUrl: 'https://psu.pogoseat.com',
        marketplaceUrlQA: 'https://psuticketsqa.pogoseat.com',
        biddingUrl: 'https://psubids.pogoseat.com',
    },
    {
        name: 'stampeders',
        id: 160,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Calgary Stampeders',
        googleAnalyticsId: 'G-L3P5J2NMRZ',
        privacyPolicy: 'https://www.cfl.ca/privacy-policy/',
        termsOfUse: 'https://www.stampeders.com/terms-of-use/',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Flames_TTBvideo.mp4',
        isDemo: false,
        seatmapUrl: 'https://stampederstickets.pogoseat.com',
        seatmapUrlQA: 'https://stampedersticketsqa.pogoseat.com',
    },

    {
        id: 161,
        name: 'essendonlogin',
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Essendon',
        privacyPolicy: 'https://www.essendonfc.com.au/privacy-policy',
        termsOfUse: 'https://am.ticketmaster.com/essendon/terms',
        isDemo: false,
        marketplaceUrl: 'https://essendon.pogoseat.com',
        marketplaceUrlQA: 'https://essendonqa.pogoseat.com',
    },
    {
        id: 162,
        name: 'carltonlogin',
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Carlton',
        privacyPolicy: 'https://www.carltonfc.com.au/privacy',
        termsOfUse: 'https://am.ticketmaster.com/carlton/terms',
        isDemo: false,
        marketplaceUrl: 'https://carlton.pogoseat.com',
        marketplaceUrlQA: 'https://carltonqa.pogoseat.com',
    },
    {
        id: 163,
        name: 'lionslogin',
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Brisbane Lions',
        privacyPolicy: 'https://www.lions.com.au/club/about/club-information/privacy-policy',
        termsOfUse: 'https://am.ticketmaster.com/brisbanelions/terms',
        isDemo: false,
        marketplaceUrl: 'https://lions.pogoseat.com',
        marketplaceUrlQA: 'https://lionsqa.pogoseat.com',
    },
    {
        id: 164,
        name: 'bulldogslogin',
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Western Bulldogs',
        privacyPolicy: 'https://www.westernbulldogs.com.au/privacy',
        termsOfUse: 'https://am.ticketmaster.com/bulldogs/terms',
        isDemo: false,
        marketplaceUrl: 'https://bulldogs.pogoseat.com',
        marketplaceUrlQA: 'https://bulldogsqa.pogoseat.com',
    },
    {
        name: 'wyndhamchampionship',
        id: 165,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'PGA TOUR',
        googleAnalyticsId: 'G-0T0R22HYNZ',
        privacyPolicy: 'https://www.pgatour.com/company/privacy-policy',
        termsOfUse: 'https://www.pgatour.com/company/tos',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Steelers_TTBvideo.mp4',
        seatmapUrl: 'https://wyndhamchampionshiptickets.pogoseat.com',
        isDemo: false,
    },
    {
        name: 'tourchampionship',
        id: 166,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Tour Championship',
        googleAnalyticsId: 'G-X5HH4NCZ78',
        privacyPolicy: 'https://www.pgatour.com/company/privacy-policy',
        termsOfUse: 'https://www.pgatour.com/company/tos',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Steelers_TTBvideo.mp4',
        isDemo: false,
    },
    // DEMO config
    {
        name: 'hsktest',
        id: 108,
        provider: 'provenue',
        sso: 'ballpark',
        isDemo: true,
        fullName: 'MLB demo',
        privacyPolicy: 'https://www.mlb.com/app/ballpark/official-information/privacy-policy',
        termsOfUse: 'https://www.mlb.com/app/ballpark/official-information/terms-of-use',
        hidePogoseatBranding: true,
    },
    {
        name: 'giantstesting',
        id: 108,
        provider: 'provenue',
        isDemo: true,
        fullName: 'San Francisco Giants Testing',
        googleAnalyticsId: '',
        privacyPolicy: 'https://mlb.com/app/ballpark/official-information/privacy-policy',
        termsOfUse: 'https://mlb.com/app/ballpark/official-information/terms-of-use',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Pogoseat_TTB_General.mp4',
        hidePogoseatBranding: true,
    },
    {
        name: 'giants-test',
        id: 71,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'San Francisco Giants Test',
        privacyPolicy: 'https://www.mlb.com/app/ballpark/official-information/privacy-policy',
        termsOfUse: 'https://www.mlb.com/app/ballpark/official-information/terms-of-use',
        marketingCheckboxOnSignup: true,
        seatmapUrl: 'https://pistonstickets.pogoseat.com',
        seatmapUrlQA: 'https://pistonstickets.qa.pogoseat.com',
        dropsUrlQA: 'https://nuggetsdrops.qa.pogoseat.com',
        isDemo: true,
    },
    {
        name: 'ttbtestingdemo',
        id: 117,
        fullName: 'TTB Testing Demo',
        isDemo: true,
        privacyPolicy: 'https://www.hendersonsilverknights.com/privacy-policy/',
        termsOfUse: 'https://www.hendersonsilverknights.com/terms-of-service/',
    },
    {
        name: 'dynamo',
        id: 37,
        provider: 'mlb',
        isDemo: true,
        fullName: 'Dynamo',
        privacyPolicy: 'https://www.mlb.com/app/ballpark/official-information/privacy-policy',
        termsOfUse: 'https://www.mlb.com/app/ballpark/official-information/terms-of-use',
    },
    {
        name: 'kings-test',
        id: 55,
        provider: 'axs',
        fullName: 'LA Kings',
        googleAnalyticsId: '',
        privacyPolicy: 'https://www.nba.com/privacy-policy',
        termsOfUse: 'https://www.nba.com/termsofuse',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Pogoseat_TTB_General.mp4',
        isDemo: true,
        defaultChannel: 'attentive',
    },
    {
        name: 'giants-test',
        id: 71,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'San Francisco Giants Test',
        privacyPolicy: 'https://www.mlb.com/app/ballpark/official-information/privacy-policy',
        termsOfUse: 'https://www.mlb.com/app/ballpark/official-information/terms-of-use',
        isDemo: true,
        marketingCheckboxOnSignup: true,
        seatmapUrl: 'https://pistonstickets.pogoseat.com',
        seatmapUrlQA: 'https://pistonsticketsqa.pogoseat.com',
    },
    {
        id: 109005,
        name: 'psu-qa',
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Penn State',
        privacyPolicy: 'https://policy.psu.edu/policies/ad53',
        termsOfUse: 'https://policy.psu.edu/policies/ad53',
        isDemo: true,
        useOptInLoginPage: true,
    },
    {
        id: 109006,
        name: 'gridiron-test',
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Gridiron Grizzlies',
        privacyPolicy: 'https://www.attentive.com/legal/privacy',
        termsOfUse: 'https://www.attentive.com/legal/terms',
        isDemo: true,
    },
    {
        name: 'jaguars',
        id: 167,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        defaultChannel: 'attentive',
        fullName: 'Jacksonville Jaguars',
        googleAnalyticsId: 'G-5D4R7QV1NG',
        privacyPolicy: 'https://www.jaguars.com/privacypolicy',
        termsOfUse: 'https://www.jaguars.com/tickets/terms',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Jags_TTBvideo.mp4',
        isDemo: false,
        dropsUrl: 'https://jaguarsdrops.pogoseat.com',
        dropsUrlQA: 'https://jaguarsdropsqa.pogoseat.com',
        seatmapUrl: 'https://jaguarstickets.pogoseat.com',
        seatmapUrlQA: 'https://jaguarsticketsqa.pogoseat.com',
    },
    {
        name: 'tdgarden',
        id: 168,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'TD Garden',
        googleAnalyticsId: 'G-YSSKYR9826',
        privacyPolicy: 'https://www.tdgarden.com/privacy-policy',
        termsOfUse: 'https://www.tdgarden.com/privacy-policy',
        promoVideoUrl: 'https://pogo-uploads-prod.s3.us-west-1.amazonaws.com/sales_channel/168/TDGarden_TTBvideo.mp4',
        isDemo: false,
        dropsUrl: 'https://tdgardendrops.pogoseat.com',
        dropsUrlQA: 'https://tdgardendropsqa.pogoseat.com',
        seatmapUrl: 'https://tdgardentickets.pogoseat.com',
        seatmapUrlQA: 'https://tdgardenticketsqa.pogoseat.com',
        useOptInLoginPage: true,
        programConsentOptIn: true,
        optInHero: {
            headline: 'Exclusive to TD Bank customers only 🎟️',
            subtext: 'Get access to last-minute ticket offers at TD Garden.',
            formHeadline: 'Get Exclusive TD Ticket Offers',
            formSubtext: 'Buy tickets in seconds via text with verified TD cardholder access.',
            accentColor: '#00a94f',
            videoUrl: 'https://pogo-uploads-prod.s3.us-west-1.amazonaws.com/sales_channel/168/TDGarden_TTBvideo.mp4',
            imageUrl:
                'https://pogo-prod-oneuploadsproddb556cb8-syd0aj1xvqwn.s3.us-west-1.amazonaws.com/sc-168/logo/d09ee75cfcdb07beadf809f90ff8d66d4dd58ccb11d8cc2340d0dd6c297f567b.png',
            mobileImageUrl:
                'https://pogo-prod-oneuploadsproddb556cb8-syd0aj1xvqwn.s3.us-west-1.amazonaws.com/sc-168/logo/bb645c1f1596e94a9e25cea7634d2f353556e94d6422d5569a43ac0f3d7948d9.png',
            imageAlt: 'Tickets are in the cards. TD Cardholders get special access to TD Garden games and concerts.',
            imageBackground: '#42bf19',
            imageOnly: true,
            formLogoUrl:
                'https://pogo-prod-oneuploadsproddb556cb8-syd0aj1xvqwn.s3.us-west-1.amazonaws.com/sc-168/logo/37526ae3a022052e7d6a44347ff1944bdd4eed35d49afba96e589f527e314d76.png',
            footerImageUrl:
                'https://pogo-prod-oneuploadsproddb556cb8-syd0aj1xvqwn.s3.us-west-1.amazonaws.com/sc-168/logo/4a505663bfbf06976569fd41f01fa6a8a7c81936298e8c0c4dd1b13d76615161.png',
            footerImageAlt: 'TD Bank, Bank of the Boston Bruins and the Boston Celtics',
        },
        embed: {
            enabled: true,
            allowedOrigins: ['https://www.tdgarden.com', 'https://td.com/'],
        },
    },
    {
        name: 'texans',
        id: 169,
        provider: 'ticketmaster',
        sso: 'ticketmaster',
        fullName: 'Houston Texans',
        googleAnalyticsId: 'G-5D4R7QV1NG',
        privacyPolicy: 'https://www.houstontexans.com/news/website-privacy-policy',
        termsOfUse: 'https://www.houstontexans.com/tickets/ticketpolicies',
        promoVideoUrl: 'https://cdn.pogoseat.com/images/videos/Jags_TTBvideo.mp4',
        isDemo: false,
        dropsUrl: 'https://texansdrops.pogoseat.com',
        dropsUrlQA: 'https://texansdropsqa.pogoseat.com',
    },
];

export const selectSalesChannel = (channel: ChannelConfig | undefined): ChannelConfig | undefined => {
    if (!channel) {
        return undefined;
    }

    if (channel.isDemo) {
        checkoutUiUrl = process.env.REACT_APP_CHECKOUT_UI_TEST_URL;
    } else {
        checkoutUiUrl = process.env.REACT_APP_CHECKOUT_UI_URL;
    }

    return channel;
};

export const getChannelById = (id: number): ChannelConfig => {
    const result = salesChannels.find((item) => item.id === id);
    return selectSalesChannel(result) as ChannelConfig;
};

export const getChannelByUrl = (url: string): ChannelConfig => {
    const subdomain = url.split('.')[0];
    const result = salesChannels.find((item) => subdomain === item.name);
    return selectSalesChannel(result) as ChannelConfig;
};
